/**
 * TypeScript loader for unified section_3d.json format.
 * 
 * Decompresses data from section_3d.json back to working format:
 * - int32 positions → float64
 * - int32 rotations → float32/matrix  
 * - int16 pixels → float64
 * 
 * Returns both legacy format (cameras[], points3D[]) and direct format.
 */

export interface Section3DData {
  metadata: {
    version: string
    format: string
    compression: {
      position_scale: number
      pixel_format: string
      removed_fields: string[]
    }
    camera_count: number
    point_count: number
  }
  camera_models: Record<number, {
    model: string
    width: number
    height: number
    fx: number
    fy: number
    cx: number
    cy: number
    k1: number
    k2: number
    p1: number
    p2: number
  }>
  cameras: Array<{
    id: number | string
    frame_file: string
    position: number[]  // int32 scaled by 1000
    rotation: number[] | number[][]
    model_id: number
  }>
  points: Array<{
    id: number
    position: number[]  // int32 scaled by 1000
    cameras: Array<[number, number, number]>  // [frame_id, pixel_x, pixel_y]
  }>
}

export interface Camera {
  id: number | string
  frame_file: string
  position: [number, number, number]
  rotation: [number, number, number, number] | number[][]
  width: number
  height: number
  intrinsics: {
    width: number
    height: number
    focal_length_x: number
    focal_length_y: number
    principal_point_x: number
    principal_point_y: number
    k1: number
    k2: number
    p1: number
    p2: number
  }
  camera_model_id: number
  camera_model: string
}

export interface Point3D {
  id: number
  position: [number, number, number]
  color?: [number, number, number]
  error?: number
  track_length?: number
  cameras?: Array<{ camera_id: number }> | Array<[number, number, number]>
}

/**
 * Load and decompress section_3d.json format.
 * 
 * Returns data in legacy format for backward compatibility with existing code.
 */
export async function loadSection3D(url: string): Promise<{
  cameras: Camera[]
  points3D: Point3D[]
  section3d: Section3DData
}> {
  // Load gzipped JSON
  const gzUrl = url.replace('.json', '.json.gz')
  console.log(`[*] Loading section_3d.json.gz from ${gzUrl}`)
  
  try {
    const response = await fetch(gzUrl)
    if (!response.ok) {
      throw new Error(`Failed to fetch: ${response.statusText}`)
    }
    
    // Get response as ArrayBuffer
    const arrayBuffer = await response.arrayBuffer()
    
    // Check if response is HTML error (some servers return 404 HTML with 200 status)
    const textSample = new TextDecoder().decode(arrayBuffer.slice(0, 200))
    if (textSample.includes('<!doctype') || textSample.includes('<html') || textSample.includes('<!DOCTYPE')) {
      throw new Error(`Server returned HTML error (possibly 404) instead of section_3d.json.gz`)
    }
    
    // Check if file is actually gzipped or plain JSON
    const firstBytes = new Uint8Array(arrayBuffer.slice(0, 2))
    const isGzipped = firstBytes[0] === 0x1f && firstBytes[1] === 0x8b
    
    console.log(`[DEBUG] Received ${arrayBuffer.byteLength} bytes, Content-Type: ${response.headers.get('content-type')}, isGzipped: ${isGzipped}`)
    
    let jsonString: string
    
    if (isGzipped) {
      // File is gzip-compressed, decompress it
      const decompressed = await decompressGzip(arrayBuffer)
      jsonString = new TextDecoder().decode(decompressed)
    } else {
      // File is plain JSON, read directly
      console.log(`[DEBUG] File is plain JSON (not gzipped), parsing directly...`)
      jsonString = new TextDecoder().decode(arrayBuffer)
    }
    
    const section3d = JSON.parse(jsonString) as Section3DData
    
    console.log(`[OK] Loaded section_3d.json.gz: ${section3d.metadata.camera_count} cameras, ${section3d.metadata.point_count} points`)
    console.log(`     Observations: ${section3d.points.reduce((sum, p) => sum + p.cameras.length, 0)}`)
    
    // Decompress to legacy format
    const { cameras, points3D } = decompressSection3D(section3d)
    
    return { cameras, points3D, section3d }
  } catch (error) {
    console.error(`❌ Error loading section_3d.json:`, error)
    throw error
  }
}

/**
 * Decompress gzipped data using pako
 */
async function decompressGzip(arrayBuffer: ArrayBuffer): Promise<Uint8Array> {
  try {
    const pako = await import('pako')
    return pako.inflate(new Uint8Array(arrayBuffer))
  } catch (error) {
    console.error('[!] Decompression failed:', error)
    throw new Error(`Failed to decompress gzipped data: ${error}`)
  }
}

/**
 * Decompress section_3d.json to legacy format.
 */
function decompressSection3D(section3d: Section3DData): {
  cameras: Camera[]
  points3D: Point3D[]
} {
  const positionScale = section3d.metadata.compression.position_scale
  const cameraModels = section3d.camera_models
  
  // Decompress cameras
  const cameras: Camera[] = section3d.cameras.map((cam) => {
    const model = cameraModels[cam.model_id]
    
    return {
      id: cam.id,
      frame_file: cam.frame_file,
      position: [
        cam.position[0] / positionScale,
        cam.position[1] / positionScale,
        cam.position[2] / positionScale,
      ] as [number, number, number],
      rotation: Array.isArray(cam.rotation[0]) 
        ? (cam.rotation as number[][])
        : (cam.rotation as [number, number, number, number]),
      width: model.width,
      height: model.height,
      intrinsics: {
        width: model.width,
        height: model.height,
        fx: model.fx,
        fy: model.fy,
        cx: model.cx,
        cy: model.cy,
        k1: model.k1,
        k2: model.k2,
        p1: model.p1,
        p2: model.p2,
      },
      camera_model_id: cam.model_id,
      camera_model: model.model,
      observed_points: [], // Initialize for reverse lookup from points
    }
  })
  
  // Decompress points
  const points3D: Point3D[] = section3d.points.map((pt) => {
    return {
      id: pt.id,
      position: [
        pt.position[0] / positionScale,
        pt.position[1] / positionScale,
        pt.position[2] / positionScale,
      ] as [number, number, number],
      // Legacy fields are removed in compression, use defaults
      color: [128, 128, 128],
      error: 0,
      track_length: pt.cameras.length,
      // Convert from direct format to legacy camera_id format
      cameras: pt.cameras.map(([frameId]) => ({ camera_id: frameId })),
    }
  })
  
  // Build observed_points for each camera (reverse lookup from point observations)
  // This allows tap-to-3D to work: for each point, add it to all cameras that see it
  for (const point of section3d.points) {
    for (const [frameId, pixelX, pixelY] of point.cameras) {
      const camera = cameras.find(c => c.id === frameId)
      if (camera) {
        // Add to observed_points array for cameraProjection.ts tap-to-3D lookup
        (camera as any).observed_points.push({
          point_id: point.id,
          pixel: [pixelX, pixelY],
        })
      }
    }
  }
  
  return { cameras, points3D }
}

/**
 * Build point cloud mappings from cameras and points.
 * 
 * Creates a lookup: for each frame, which points are visible and at what pixels.
 */
export function buildPointCloudMappings(
  cameras: Camera[],
  points3D: Point3D[]
): Map<number, Array<{ pointId: number; pixel: [number, number] }>> {
  const mappings = new Map<number, Array<{ pointId: number; pixel: [number, number] }>>()
  
  // For each point, add its observations to the corresponding frame
  for (const point of points3D) {
    if (!point.cameras) continue
    
    // Handle both formats: legacy {camera_id} and direct [frameId, px, py]
    for (const cam of point.cameras) {
      const frameId = Array.isArray(cam) ? cam[0] : cam.camera_id
      const pixel = Array.isArray(cam) ? ([cam[1], cam[2]] as [number, number]) : ([0, 0] as [number, number])
      
      if (!mappings.has(frameId)) {
        mappings.set(frameId, [])
      }
      
      mappings.get(frameId)!.push({
        pointId: point.id,
        pixel,
      })
    }
  }
  
  return mappings
}

/**
 * KD-Tree node for spatial queries.
 */
interface KDNode {
  point: Point3D
  left?: KDNode
  right?: KDNode
  depth: number
}

/**
 * Build KD-Tree from points for fast spatial queries.
 */
export function createPointKDTree(points: Point3D[]): KDNode | null {
  if (points.length === 0) return null
  
  function buildTree(items: Point3D[], depth: number): KDNode | null {
    if (items.length === 0) return null
    
    const axis = depth % 3  // x, y, z
    const sorted = [...items].sort((a, b) => {
      const aVal = a.position[axis]
      const bVal = b.position[axis]
      return aVal - bVal
    })
    
    const median = Math.floor(sorted.length / 2)
    
    return {
      point: sorted[median],
      depth,
      left: buildTree(sorted.slice(0, median), depth + 1),
      right: buildTree(sorted.slice(median + 1), depth + 1),
    }
  }
  
  return buildTree(points, 0)
}

/**
 * Find nearest neighbors in KD-Tree.
 */
export function findNearestPoints(
  tree: KDNode | null,
  query: [number, number, number],
  maxDistance: number,
  k: number = 1
): Point3D[] {
  if (!tree) return []
  
  const candidates: Array<{ point: Point3D; distance: number }> = []
  
  function search(node: KDNode | null, depth: number) {
    if (!node) return
    
    const axis = depth % 3
    const diff = query[axis] - node.point.position[axis]
    
    // Calculate distance
    const dx = query[0] - node.point.position[0]
    const dy = query[1] - node.point.position[1]
    const dz = query[2] - node.point.position[2]
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
    
    if (distance <= maxDistance) {
      candidates.push({ point: node.point, distance })
    }
    
    const nearNode = diff < 0 ? node.left : node.right
    const farNode = diff < 0 ? node.right : node.left
    
    search(nearNode, depth + 1)
    
    if (Math.abs(diff) <= maxDistance) {
      search(farNode, depth + 1)
    }
  }
  
  search(tree, 0)
  
  return candidates
    .sort((a, b) => a.distance - b.distance)
    .slice(0, k)
    .map((c) => c.point)
}

/**
 * Build camera KD-Tree for finding closest cameras.
 */
interface CameraKDNode {
  camera: Camera
  index: number
  left?: CameraKDNode
  right?: CameraKDNode
  depth: number
}

export function createCameraKDTree(cameras: Camera[]): CameraKDNode | null {
  if (cameras.length === 0) return null
  
  function buildTree(indices: number[], depth: number): CameraKDNode | null {
    if (indices.length === 0) return null
    
    const axis = depth % 3
    const sorted = [...indices].sort((a, b) => {
      const aVal = cameras[a].position[axis]
      const bVal = cameras[b].position[axis]
      return aVal - bVal
    })
    
    const median = Math.floor(sorted.length / 2)
    
    return {
      camera: cameras[sorted[median]],
      index: sorted[median],
      depth,
      left: buildTree(sorted.slice(0, median), depth + 1),
      right: buildTree(sorted.slice(median + 1), depth + 1),
    }
  }
  
  const indices = cameras.map((_, i) => i)
  return buildTree(indices, 0)
}

/**
 * Find nearest cameras in KD-Tree.
 */
export function findNearestCameras(
  tree: CameraKDNode | null,
  query: [number, number, number],
  maxDistance: number,
  k: number = 1
): Camera[] {
  if (!tree) return []
  
  const candidates: Array<{ camera: Camera; distance: number }> = []
  
  function search(node: CameraKDNode | null, depth: number) {
    if (!node) return
    
    const axis = depth % 3
    const diff = query[axis] - node.camera.position[axis]
    
    // Calculate distance
    const dx = query[0] - node.camera.position[0]
    const dy = query[1] - node.camera.position[1]
    const dz = query[2] - node.camera.position[2]
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz)
    
    if (distance <= maxDistance) {
      candidates.push({ camera: node.camera, distance })
    }
    
    const nearNode = diff < 0 ? node.left : node.right
    const farNode = diff < 0 ? node.right : node.left
    
    search(nearNode, depth + 1)
    
    if (Math.abs(diff) <= maxDistance) {
      search(farNode, depth + 1)
    }
  }
  
  search(tree, 0)
  
  return candidates
    .sort((a, b) => a.distance - b.distance)
    .slice(0, k)
    .map((c) => c.camera)
}

/**
 * Utility: Decompress position from section_3d format.
 */
export function decompressPosition(compressed: number[], scale: number = 1000): [number, number, number] {
  return [
    compressed[0] / scale,
    compressed[1] / scale,
    compressed[2] / scale,
  ]
}

/**
 * Utility: Decompress pixel coordinates from section_3d format.
 */
export function decompressPixel(compressed: [number, number]): [number, number] {
  return [compressed[0], compressed[1]]  // Already int16, no decompression needed
}
